import { Combobox, createListCollection, Text } from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'
import type { SelectOption } from '../types/phoneNumber.types'

const NONE_VALUE = '__none__'

interface IntegrationOption {
  id: string
  name: string
}

interface IntegrationComboboxProps {
  integrations: SelectOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  t?: PhoneNumbersTranslateFn
}

/**
 * Searchable combobox for picking an SMS relay integration from a PBX-scoped
 * list. Renders a "None" option so users can clear the selection without
 * needing a separate clear affordance. Filtering is client-side because the
 * per-PBX integration list is small (typically 0-5 entries).
 *
 * The "None" option is always visible regardless of the search term so users
 * can always clear the selection.
 */
export const IntegrationCombobox: React.FC<IntegrationComboboxProps> = ({
  integrations,
  value,
  onChange,
  disabled,
  t,
}) => {
  const noneLabel = text(t, 'phoneNumbers.edit.noIntegration', 'None')
  const searchPlaceholder = text(
    t,
    'phoneNumbers.edit.integrationSearchPlaceholder',
    'Search integrations...',
  )
  const emptyLabel = text(
    t,
    'phoneNumbers.edit.noIntegrationMatches',
    'No integration matches that search.',
  )
  const searchAriaLabel = text(
    t,
    'phoneNumbers.edit.integrationSearchAriaLabel',
    'Search SMS relay integrations by name',
  )

  const integrationOptions = useMemo<IntegrationOption[]>(
    () =>
      integrations.map((ig) => ({
        id: ig.id.toString(),
        name: ig.name,
      })),
    [integrations],
  )

  const selectedOption = useMemo(() => {
    if (!value) return null
    return integrationOptions.find((option) => option.id === value) ?? null
  }, [integrationOptions, value])

  const [inputValue, setInputValue] = useState(selectedOption?.name ?? '')

  // See ExtensionCombobox for the rationale: `onOpenChange` fires after
  // `onValueChange` with a stale `selectedOption` closure and would
  // otherwise clobber the freshly-set inputValue with the previous
  // selection's label.
  const justSelectedRef = useRef(false)

  // Keep the rendered label in sync when the value changes from outside
  // (e.g. reset on modal open) or when `integrations` loads late and can now
  // resolve the selected option's name.
  useEffect(() => {
    setInputValue(selectedOption?.name ?? '')
  }, [selectedOption])

  const filteredIntegrations = useMemo(() => {
    const trimmed = inputValue.trim().toLowerCase()
    const matchesSelected =
      !!selectedOption && selectedOption.name.trim().toLowerCase() === trimmed

    // While the user is typing a term that isn't just the current selection,
    // apply filtering. When the input matches the current selection (e.g. the
    // list was just opened), show everything.
    if (!trimmed || matchesSelected) return integrationOptions

    return integrationOptions.filter((option) =>
      option.name.toLowerCase().includes(trimmed),
    )
  }, [integrationOptions, inputValue, selectedOption])

  // "None" is always visible so users can clear the selection even while
  // filtering by an unrelated search term.
  const visibleOptions = useMemo<IntegrationOption[]>(
    () => [{ id: NONE_VALUE, name: noneLabel }, ...filteredIntegrations],
    [filteredIntegrations, noneLabel],
  )

  const collection = useMemo(
    () =>
      createListCollection<IntegrationOption>({
        items: visibleOptions,
        itemToValue: (item) => item.id,
        itemToString: (item) => item.name,
      }),
    [visibleOptions],
  )

  const comboboxValue = value ? [value] : [NONE_VALUE]

  const hasSearchTerm = inputValue.trim().length > 0
  const isSearchMatchingSelected =
    !!selectedOption &&
    selectedOption.name.trim().toLowerCase() === inputValue.trim().toLowerCase()
  const showNoMatchesHint =
    hasSearchTerm &&
    !isSearchMatchingSelected &&
    filteredIntegrations.length === 0

  return (
    <Combobox.Root
      collection={collection}
      value={comboboxValue}
      inputValue={inputValue}
      onValueChange={(details) => {
        const next = details.value[0] ?? ''
        justSelectedRef.current = true
        if (!next || next === NONE_VALUE) {
          onChange('')
          setInputValue('')
          return
        }
        onChange(next)
        const match = integrationOptions.find((option) => option.id === next)
        setInputValue(match?.name ?? '')
      }}
      onInputValueChange={(details) => {
        setInputValue(details.inputValue)
      }}
      onOpenChange={(details) => {
        if (details.open) return
        if (justSelectedRef.current) {
          justSelectedRef.current = false
          return
        }
        // Popover closed without a selection (escape, click-away, blur).
        // Wipe any stray search term so we don't leave a partial query
        // showing the wrong label in the field.
        setInputValue(selectedOption?.name ?? '')
      }}
      openOnClick
      disabled={disabled}
      width="100%"
      size="md"
    >
      <Combobox.Control>
        <Combobox.Input
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          // Select the existing label on focus so typing immediately
          // replaces it instead of appending. Deferred to the next
          // animation frame because Ark UI's own focus handler runs
          // after this one and resets the cursor — calling `select()`
          // synchronously here gets undone on the same tick.
          onFocus={(e) => {
            const input = e.currentTarget
            requestAnimationFrame(() => input.select())
          }}
        />
        <Combobox.IndicatorGroup>
          <Combobox.Trigger>
            <ChevronsUpDown />
          </Combobox.Trigger>
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content maxH="280px" overflowY="auto">
          {showNoMatchesHint && (
            <Text
              px={3}
              py={2}
              fontSize="sm"
              color="fg.muted"
              role="status"
              aria-live="polite"
            >
              {emptyLabel}
            </Text>
          )}
          {visibleOptions.map((option) => (
            <Combobox.Item key={option.id} item={option}>
              <Combobox.ItemText>{option.name}</Combobox.ItemText>
              <Combobox.ItemIndicator>
                <Check />
              </Combobox.ItemIndicator>
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  )
}
